import React, { useCallback, useEffect, useState } from 'react';
import { MutationStatus } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Divider, ErrorInfo, Grid, KeyValue, SectionHeader, Typography } from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import { UploadedFile } from 'web-library/models/UploadedFile';
import TaskFileBadge from 'web-library/components/TaskFileBadge';
import { useGetSubmittedWithdrawal } from '../../api/candidate/exam/@candidate/queries-services/queries';
import RouteKey from '../../routes/key';
import UploadDocuments from '../../pages/accessArrangement/SelectCondition/components/UploadDocuments';
import useUploadDocument from './logic/useUploadDocument';
import usePostPresignLinks from './logic/usePostPresignLinks';
import { ApplicationType } from '../../api/candidate/config/model';
import useSetUploadButtonStatus from '../../hooks/useSetUploadButtonStatus';
import WithdrawalReasonButtons from './WithdrawalReasonButtons';
import { EntityModelList, GetWithdrawalDraftResponse } from '../../api/candidate/exam/model';
import { useGetApplicationFilesConfig } from '../../api/document/attachedFiles/queries-services/queries';
import { useGetDocumentUploadStatus } from '../../api-refactored/document/useGetDocumentUploadStatus';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  documentsContainer: {
    marginTop: theme.spacing(1.5),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  buttonContainer: {
    display: 'flex',
    marginBottom: theme.spacing(5),
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    color: theme.color.text.textHeader,
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  marginBottom: {
    marginBottom: theme.spacing(3),
  },
  errString: {
    color: theme.color.state.stateError,
  },
}));

const { withdrawal } = RouteKey.child;
const { examRegistrations, review, summary } = RouteKey.withdrawal.child;

export const useDisplayFilesExceedError = (
  withdrawalData: GetWithdrawalDraftResponse | undefined,
  setFormErrors: React.Dispatch<React.SetStateAction<string[]>>,
  t: TFunction<'translation'>
) => {
  const { data: applicationFilesConfig } = useGetApplicationFilesConfig(ApplicationType.EXAM);

  useEffect(() => {
    if (
      applicationFilesConfig &&
      withdrawalData &&
      withdrawalData.fileEntityModelList?.length !== undefined &&
      withdrawalData.fileEntityModelList?.length === applicationFilesConfig.fileNumLimit &&
      withdrawalData.fileEntityModelList.every((file: EntityModelList) => !file.draft)
    ) {
      setFormErrors([t('specialConsideration->>ApplyForSpecialConsideration.FilesExceededError')]);
    }
  }, [applicationFilesConfig, withdrawalData, t, setFormErrors]);
};

const UploadAdditionalDocument = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid } = useParams<any>();
  const history = useHistory();

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [displayErrors, setDisplayErrors] = useState<boolean>(false);
  const [filesSubmitted, setFilesSubmitted] = useState<UploadedFile[]>();

  const [fileUuid, setFileUuid] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const { data: withdrawalData } = useGetSubmittedWithdrawal(uuid, (res) => {
    if (res.fileEntityModelList) {
      const filesDraft: UploadedFile[] = [];
      const submittedFiles: UploadedFile[] = [];

      res.fileEntityModelList.forEach((file) => {
        const uploadedFile = {
          file: new File([new ArrayBuffer(file.fileSize)], file.fileName),
          hasError: false,
          pending: false,
          uploaded: true,
          key: file.uuid,
        };

        if (file.draft) {
          filesDraft.push(uploadedFile);
        } else {
          submittedFiles.push(uploadedFile);
        }
      });

      setDraftFiles(filesDraft);
      setFilesSubmitted(submittedFiles);
    }
  });

  const usePostPresignLinksMutation = usePostPresignLinks({
    files,
    setFiles,
    setShouldSubmit,
    setApiButtonStatus,
    setErrorMsg,
  });

  const { getFilenames } = useUploadDocument({
    files,
    setFiles,
  });

  useEffect(() => {
    const newErrors = [];

    if (draftFiles.length < 1 && files.length < 1) {
      newErrors.push(t('withdrawal->>AdditionalDocument.NoDocumentErrMessage'));
    }
    setFormErrors(newErrors);
  }, [draftFiles, files, t]);

  const handleReview = useCallback(() => {
    history.push(`/${withdrawal}/${examRegistrations.route}/${uuid}/${review.route}`);
  }, [history, uuid]);

  useSetUploadButtonStatus({
    files,
    setApiButtonStatus,
    setShouldSubmit,
    shouldSubmit,
    onSuccessCallback: handleReview,
  });

  const handleBack = () =>
    history.push(`/${withdrawal}/${examRegistrations.route}/${uuid}/${summary.route}`);

  const handleConfirm = () => {
    if (formErrors.length < 1) {
      if (files && files.length > 0) {
        usePostPresignLinksMutation.mutate({
          payload: {
            applicationType: 'EXAM',
            uuid,
            filenames: getFilenames(),
          },
        });
      } else {
        handleReview();
      }
    } else {
      setDisplayErrors(true);
    }
  };

  useDisplayFilesExceedError(withdrawalData, setFormErrors, t);

  useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && fileUuid !== '',
    refetchInterval: undefined,
    onSuccess: (res) => {
      if (res.url) {
        const newWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
        setFileUuid('');
      }
    },
  });

  return (
    <>
      <SectionHeader title={t('withdrawal->>ReviewWithdrawal.ReasonForWithdrawal')} />

      <Grid>
        <KeyValue
          id="reasonForWithdrawal"
          keyText={t('withdrawal->>AdditionalDocument.SubmittedReason')}
          valueText={withdrawalData?.reason.reasonDescription ?? '-'}
          capitalize={false}
        />
      </Grid>
      {withdrawalData?.specifiedReason && (
        <Grid>
          <KeyValue
            id="reasonForWithdrawal"
            keyText={t('withdrawal->>ReviewWithdrawal.SpecifiedReason')}
            valueText={withdrawalData?.specifiedReason}
            capitalize={false}
          />
        </Grid>
      )}
      <Grid>
        <KeyValue
          id="attachedFile"
          keyText={t('withdrawal->>AdditionalDocument.SubmittedDocuments')}
          valueText={
            <Grid className={classes.documentsContainer}>
              {filesSubmitted?.length === 0 ? (
                <>-</>
              ) : (
                <>
                  {filesSubmitted?.map((file, index) => (
                    <TaskFileBadge
                      key={file.key}
                      file={{
                        fileUuid: file.key as string,
                        fileName: file.file.name,
                        fileSizeInBytes: file.file.size,
                        submitted: true,
                      }}
                      setFileUuid={setFileUuid}
                    />
                  ))}
                </>
              )}
            </Grid>
          }
          capitalize={false}
        />
      </Grid>

      <Divider />

      <Typography className={classes.header} variant="h3">
        {t('withdrawal->>WithdrawalReason.UploadSupportingDocuments')}
      </Typography>

      <Typography className={classes.inputLabel} variant="h5">
        {t('withdrawal->>WithdrawalReason.SupportingDocumentRequired')}
      </Typography>

      <Typography className={classes.inputLabel} variant="body1">
         {t('withdrawal->>WithdrawalReason.uploadInstruction')}
      </Typography>

      <Box className={classes.marginBottom}>
        {withdrawalData?.reason.requiredDocumentInfo.map((requiredDocument: string) => (
          <Typography key={requiredDocument}>{requiredDocument}</Typography>
        ))}
      </Box>

      {filesSubmitted && (
        <>
          <UploadDocuments
            files={files}
            setFiles={setFiles}
            hasError={displayErrors}
            submittedFiles={filesSubmitted.length}
            applicationType={ApplicationType.EXAM}
            draftFiles={draftFiles}
            setDraftFiles={setDraftFiles}
          />
          <ErrorInfo label={errorMsg} />
        </>
      )}

      <Box className={classes.buttonContainer}>
        <WithdrawalReasonButtons
          files={files}
          apiButtonStatus={apiButtonStatus}
          textArea={withdrawalData?.specifiedReason}
          selectReason={withdrawalData?.reason.reasonDescription}
          displayErrors={displayErrors}
          setDisplayErrors={setDisplayErrors}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          handleBack={handleBack}
          handleConfirm={handleConfirm}
        />
      </Box>
    </>
  );
};

export default UploadAdditionalDocument;
